import React, { useState } from 'react';
import {
  Box,
  Image,
  Text,
  Heading,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Divider,
  Button,
} from '@chakra-ui/react';
import { MapPin } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

const StatCell = ({ label, value, valueColor = '#F5F7FA', bordered = false }) => (
  <Box
    pl={bordered ? { base: 0, sm: 4 } : 0}
    borderLeft={bordered ? { base: 'none', sm: '1px solid' } : 'none'}
    borderColor="rgba(255,255,255,0.08)"
  >
    <Text
      fontFamily="heading"
      fontWeight={500}
      fontSize="xl"
      color={valueColor}
      lineHeight={1.1}
      letterSpacing="0"
    >
      {value}
    </Text>
    <Text fontFamily="body" fontSize="xs" color="#9AA7B8" mt={1.5}>
      {label}
    </Text>
  </Box>
);

const EnhancedPropertyCard = ({
  id,
  image,
  title,
  location,
  roi,
  value,
  funded,
  tag,
  type = 'residential',
  details = {},
  ...props
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const reducedMotion = useReducedMotion();

  const fundedValue = parseInt(String(funded).replace('%', ''), 10) || 0;
  const minInvestment = details?.minInvestment || '—';
  const occupancyRate = details?.occupancyRate || '—';

  return (
    <>
      <Box
        role="group"
        bg="#0E2242"
        borderRadius="8px"
        border="1px solid"
        borderColor="rgba(255,255,255,0.08)"
        overflow="hidden"
        transition="all 0.3s cubic-bezier(0.16, 1, 0.3, 1)"
        cursor="pointer"
        onClick={() => setIsModalOpen(true)}
        _hover={{
          transform: 'translateY(-4px)',
          borderColor: 'rgba(212,168,95,0.32)',
          boxShadow: '0 16px 40px rgba(0,0,0,0.40), 0 0 28px rgba(212,168,95,0.18)',
        }}
        {...props}
      >
        {/* Image header with tag pill */}
        <Box position="relative" h={{ base: '180px', md: '165px' }} overflow="hidden">
          <Image
            src={image}
            alt={title}
            w="100%"
            h="100%"
            objectFit="cover"
            filter="saturate(0.8) contrast(1.05)"
            transition="transform 0.5s ease"
            _groupHover={{ transform: 'scale(1.04)' }}
          />
          {tag && (
            <Box
              position="absolute"
              top={4}
              left={4}
              px={3}
              py={1.5}
              borderRadius="6px"
              bg="linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)"
              color="#071326"
              fontFamily="body"
              fontWeight={600}
              fontSize="11px"
              letterSpacing="0.08em"
              textTransform="uppercase"
            >
              {tag}
            </Box>
          )}
        </Box>

        {/* Body */}
          <Box p={{ base: 5, md: 5 }}>
          <Heading
            as="h3"
            fontFamily="heading"
            fontWeight={500}
            fontSize={{ base: 'xl', md: '1.25rem' }}
            color="#F5F7FA"
            lineHeight={1.2}
            letterSpacing="0"
            noOfLines={2}
          >
            {title}
          </Heading>

          <HStack spacing={2} mt={2} color="#D4A85F">
            <Icon as={MapPin} boxSize={3} strokeWidth={1.7} />
            <Text fontFamily="body" fontSize="xs" color="#D6DCE5">
              {location}
            </Text>
          </HStack>

          {/* Stats row */}
          <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={{ base: 3, sm: 4 }} mt={5}>
            <StatCell label="Projected Yield" value={roi} />
            <StatCell label="Min. Investment" value={minInvestment} bordered />
            <StatCell label="Occupancy" value={occupancyRate} bordered />
          </SimpleGrid>

          {/* Funding bar */}
          <Box mt={5}>
            <Text
              fontFamily="body"
              fontWeight={600}
              fontSize="11px"
              color="#D4A85F"
              letterSpacing="0.08em"
              textTransform="uppercase"
              mb={2}
            >
              {funded} Funded
            </Text>
            <Box w="100%" h="4px" bg="rgba(255,255,255,0.08)" borderRadius="full" overflow="hidden">
              <Box
                as={motion.div}
                h="100%"
                bg="linear-gradient(135deg, #D4A85F 0%, #E0B873 100%)"
                borderRadius="full"
                initial={{ width: reducedMotion ? `${fundedValue}%` : '0%' }}
                whileInView={{ width: `${fundedValue}%` }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{
                  duration: reducedMotion ? 0 : 1.1,
                  ease: [0.16, 1, 0.3, 1],
                  delay: reducedMotion ? 0 : 0.2,
                }}
              />
            </Box>
          </Box>

          {/* View Details link */}
          <HStack mt={5} justify="space-between" align="center">
            <Text
              fontFamily="body"
              fontSize="sm"
              color="#D4A85F"
              fontWeight={500}
              transition="all 0.2s ease"
              _groupHover={{ color: '#E0B873' }}
            >
              View Details →
            </Text>
          </HStack>
        </Box>
      </Box>

      {/* Details modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} isCentered size="lg">
        <ModalOverlay bg="rgba(7,19,38,0.7)" backdropFilter="blur(8px)" />
        <ModalContent bg="#0E2242" border="1px solid" borderColor="rgba(255,255,255,0.08)" color="#F5F7FA">
          <ModalHeader fontFamily="heading" fontWeight={500} fontSize="xl" color="#F5F7FA">
            {title}
          </ModalHeader>
          <ModalCloseButton color="#D4A85F" />
          <ModalBody pb={8}>
            <VStack spacing={4} align="stretch" fontSize="sm">
              <HStack spacing={2} color="#9AA7B8">
                <Icon as={MapPin} boxSize={3} strokeWidth={1.7} />
                <Text fontFamily="body">{location}</Text>
              </HStack>

              <Divider borderColor="rgba(255,255,255,0.08)" />

              <SimpleGrid columns={3} spacing={4}>
                <StatCell label="Projected Yield" value={roi} valueColor="#D4A85F" />
                <StatCell label="Min. Investment" value={minInvestment} />
                <StatCell label="Occupancy" value={occupancyRate} />
              </SimpleGrid>

              <Divider borderColor="rgba(255,255,255,0.08)" />

              <VStack spacing={2.5} align="stretch" color="#9AA7B8">
                {details.propertyType && (
                  <HStack justify="space-between">
                    <Text>Type</Text>
                    <Text color="#F5F7FA" fontWeight={500}>{details.propertyType}</Text>
                  </HStack>
                )}
                {type === 'residential' && details.bedrooms && (
                  <HStack justify="space-between">
                    <Text>Bedrooms</Text>
                    <Text color="#F5F7FA" fontWeight={500}>{details.bedrooms}</Text>
                  </HStack>
                )}
                {details.sqft && (
                  <HStack justify="space-between">
                    <Text>Size</Text>
                    <Text color="#F5F7FA" fontWeight={500}>{details.sqft} sqm</Text>
                  </HStack>
                )}
                {details.NoofUnits && (
                  <HStack justify="space-between">
                    <Text>Number of Units</Text>
                    <Text color="#F5F7FA" fontWeight={500}>{details.NoofUnits}</Text>
                  </HStack>
                )}
                {details.yearBuilt && (
                  <HStack justify="space-between">
                    <Text>Year Built</Text>
                    <Text color="#F5F7FA" fontWeight={500}>{details.yearBuilt}</Text>
                  </HStack>
                )}
                {details.rentalYield && (
                  <HStack justify="space-between">
                    <Text>Rental Yield</Text>
                    <Text color="#D4A85F" fontWeight={600}>{details.rentalYield}</Text>
                  </HStack>
                )}
                {details.investors !== undefined && (
                  <HStack justify="space-between">
                    <Text>Investors</Text>
                    <Text color="#F5F7FA" fontWeight={500}>{details.investors}</Text>
                  </HStack>
                )}
              </VStack>

              {details.description && (
                <Box>
                  <Text color="#9AA7B8" mb={1.5}>Description</Text>
                  <Text color="#D6DCE5" fontSize="xs" lineHeight={1.7}>
                    {details.description}
                  </Text>
                </Box>
              )}

              <Button
                as={RouterLink}
                to="/login?message=login-to-view-more-details"
                variant="primary"
                size="md"
                w="100%"
                mt={2}
                borderRadius="6px"
              >
                Sign In to View Full Details
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EnhancedPropertyCard;
